import React from "react"
import { Link } from "gatsby"
import { Helmet } from "react-helmet"
import Layout from "../../components/layout-en"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import ImageChirurgien from "../../components/imageChirurgien-en"
import ImageSurMesure from "../../components/imageSurMesure-en"
import ImageTechniques from "../../components/imageTechniques-en"
import ImageConventionne from "../../components/imageConventionne-en"
import SEO from "../../components/seo-en"
import Share from "../../components/share-en"

const IndexPage = () => (
  <Layout pathFr="/chirurgien-main-coude-epaule-paris/">
    <SEO
      title="Orthopedic surgeon of the upper limb in Paris - Dr Roure"
      description=" Orthopaedic surgeon in Paris 7, Dr Roure is qualified in surgery of the
      hand, the elbow, the wrist and the shoulder."
    />
    <Helmet>
      <link
        rel="alternate"
        hreflang="fr"
        href={`https://www.docteurphilipperoure.com/chirurgien-main-coude-epaule-paris/`}
      />
    </Helmet>
    <ImageChirurgien />
    <WrapperTitle>
      <Breadcrumb>
        <Link to="/en/">Home </Link>
      </Breadcrumb>
      <H1>Dr Philippe Roure, Orthopedic surgeon of the upper limb in Paris</H1>
      <p className="intro">
        Dr. Philippe Roure is a surgeon specialising in orthopaedic and
        restorative surgery, with more than 20&nbsp;years of experience
        exclusively as an upper-limb surgeon, performing hand, elbow, shoulder
        and wrist surgery in Paris. He graduated in microsurgery, sports surgery
        and hand surgery.
      </p>
    </WrapperTitle>

    <Wrapper>
      <Row>
        <Col1>
          <H2>
            A <span>tailor-made</span> approach to surgery
          </H2>
          <p>
            Dr. Philippe Roure favours a "tailor-made" approach to orthopaedic
            <Link to="en/hand-surgery/"> hand</Link>,{" "}
            <Link to="/en/elbow-surgery/">elbow</Link> and{" "}
            <Link to="/en/shoulder-surgery/">shoulder</Link> surgery, seeking
            both the most technically advanced solution and the one that{" "}
            <strong>
              best matches the expectations and profile of his patients
            </strong>
            .
          </p>

          <p>
            He combines his experience in the treatment of high-performance
            athletes with restorative surgery and microsurgery in order to offer
            a <strong>personalised response</strong> to each of his patients.
          </p>

          <p>
            His <strong>20 years of experience</strong> exclusively as an upper
            limb, hand and shoulder surgeon give him the necessary vantage point
            to offer his patients the most relevant solutions.
          </p>

          <p>
            He <strong>personally performs</strong> all procedures and carries
            out the
            <strong> post-operative follow-up</strong> for all of his patients.
          </p>
        </Col1>
        <Col2>
          <ImageSurMesure />
        </Col2>
      </Row>

      <Row>
        <Col3>
          <ImageTechniques />
        </Col3>
        <Col4>
          <H2>
            <span>Minimally-invasive</span> techniques
          </H2>
          <p>
            Dr. Roure uses the very latest, and least invasive techniques for
            his procedures, particularly involving{" "}
            <Link to="/en/technics/microsurgery/">microsurgery</Link> and{" "}
            <Link to="/en/technics/arthroscopy/">arthroscopy</Link>. These
            techniques <strong>minimise pain</strong> and post-operative
            down-time, while yielding{" "}
            <strong>optimal functional and aesthetic results</strong>.
          </p>
          <p>
            His consultations and surgical procedures take place in
            highly-specialised Parisian orthopaedic surgery and sports surgery
            centres: the{" "}
            <Link to="en/consultation/">Espace Médical Vauban</Link> (Paris 7
            <sup>th</sup> arrondissement) and the{" "}
            <Link to="/en/clinic/">Clinique du Sport</Link> (Paris 5
            <sup>th</sup> arrondissement).
          </p>
          <p>
            This means he uses the very latest surgical equipment with the{" "}
            <strong>highest safety and quality standards</strong>, working
            together with colleagues who are also trauma, sports medicine and
            orthopaedic surgery specialists.
          </p>
        </Col4>
      </Row>

      <Row>
        <Col5>
          <H2>
            An <span>experienced</span> and <span>highly-specialised</span>{" "}
            surgeon
          </H2>
          <p>
            Dr. Roure has been in private practice <strong>for 20 years</strong>{" "}
            and has a private health care contract with the French social
            security (sector 2). To remain fully independent and free to use
            those surgical techniques he considers the most suitable, he does
            not practice under the <strong>OPTAM / OPTAM CO</strong> contracts
            offered by the French social security bodies.
          </p>
          <p>
            If your mutual insurance contract does not cover you fully for
            surgical costs, a portion of the fees may need to be paid by you. If
            you do not have French social security but a private insurance, a
            <strong> precise quote</strong> will be established as soon as
            possible for your care. His assistant Paula is on hand to go through
            your cover with you. You will always be issued with a quote prior to
            any procedure.
          </p>
          <p>
            Dr. Philippe Roure is registered with the Departmental Council of
            the Order of Physicians of the City of Paris, under Reg. No. 73755,
            and No. RPPS 10000832088. He works through his company SELARL
            PHILIPPE ROURE, 448029561 RCS Paris, of which he is the managing
            director.
          </p>
        </Col5>
        <Col6>
          <ImageConventionne />
        </Col6>
      </Row>

      <H3>
        Titles & <span>qualifications</span>
      </H3>
      <Row
        css={css`
          margin-top: -10px;
        `}
      >
        <Col>
          <Ul>
            <Li>
              Diplomé du DESC de chirurgie orthopédique et traumatologique.
            </Li>
            <Li>Droit au titre de chirurgien de la main</Li>
            <Li>Ancien chef de clinique à la Faculté de Médecine de Paris</Li>
            <Li>
              Ancien assistant à l’hôpital Boucicaut et à l’hôpital Européen
              Georges Pompidou
            </Li>
            <Li>Ancien attaché à l’Hôpital Européen Georges Pompidou</Li>
            <Li>Ancien Interne des hôpitaux de Paris</Li>
            <Li>Lauréat de la faculté de Médecine de Paris</Li>
            <Li>
              Ancien attaché de recherche à Hong Kong University et assistant au
              Queen Mary Hospital de Hong Kong
            </Li>
          </Ul>
        </Col>

        <Col>
          <Ul>
            <Li>
              Diplôme Universitaire de chirurgie de la Main et du Membre
              supérieur, Université Paris VII
            </Li>
            <Li>
              Diplôme Universitaire de Microchirurgie, Université Paris XIII
            </Li>
            <Li>
              Diplôme Universitaire de Chirurgie du Sport, Université Paris VI
            </Li>
            <Li>
              Diplôme Universitaire d’anatomie fonctionnelle et chirurgicale de
              l’appareil locomoteur, Université Paris V
            </Li>
            <Li>Membre de la Société Française d’arthroscopie</Li>
            <Li>
              Membre associé de la société Française de Chirurgie de la Main
            </Li>
            <Li>
              Membre du Groupe Européen d’Etude de l’Arthroscopie du Poignet
            </Li>
            <Li>Membre de la Société Française des Services Urgence Main</Li>
          </Ul>
        </Col>
      </Row>

      <H3>
        <span>Scientific</span> publications
      </H3>

      <Ul>
        <Li>
          P. Roure, D. Fontès, J.-M. Cognet, «&nbsp;Arthroscopie des
          articulations de la main. Installations, voies d’abord et
          pathologies&nbsp;», <i>L’Arthroscopie</i>, SFA, Potel J.F, Hulet C,
          Elsevier Masson, 2015, pp.&nbsp;650-656.
        </Li>

        <Li>
          J.-M. Cognet, P. Roure, M. Levadoux, «&nbsp;Ostéosynthèse des
          fractures articulaires du radius distal sous arthroscopie&nbsp;»,{" "}
          <i>L’Arthroscopie</i>, SFA, Potel J.F, Hulet C, Elsevier Masson, 2015,
          pp.&nbsp;570-583.
        </Li>

        <Li>
          J.-M. Cognet, M. Levadoux, P. Roure, X. Martinache, «&nbsp;Syndrome du
          canal carpien. Traitement endoscopique, indications et
          résultats&nbsp;», <i>L’Arthroscopie</i>, SFA, Potel J.F, Hulet C,
          Elsevier Masson, 2015, pp.&nbsp;632-649.
        </Li>

        <Li>
          P. Roure, N. Lefevre, S. Herman, D. Fontes, «&nbsp;Notre expérience de
          l’ostéoynthèse par plaque ITS des fractures du 1/3 moyen de la
          clavicule&nbsp;»,{" "}
          <i>
            La ceinture claviculo-scapulaire : actualités thérapeutiques. 3èmes
            rencontres de l’IMM
          </i>
          , Allieu Y. Sauramps Medical 2010, pp.&nbsp;87-92.
        </Li>

        <Li>
          P. Roure, D. Fontes, «&nbsp;Les lésions articulaires de la main du
          rugbyman&nbsp;», <i>Journal de Traumatologie du Sport</i>, 2007, 24,
          pp.&nbsp;172-175.
        </Li>

        <Li>
          P. Roure, D. Fontes, «&nbsp;Complication et prévention de
          l’arthroscopie du membre supérieur&nbsp;»,{" "}
          <i>Chirurgie de la Main, n25</i>, 2006, pp.&nbsp;S274-S279.
        </Li>

        <Li>
          P. Roure, D. Fontes, «&nbsp;Apport de l’arthroscopie au niveau de
          l’articulation trapézo-métacarpienne&nbsp;»,{" "}
          <i>Journal de Traumatologie du Sport</i>, 2005, 22, pp.&nbsp;243-245.
        </Li>

        <Li>
          P. Roure, D. Fontes, «&nbsp;Aux limites de l’arthroscopie : le canal
          carpien endoscopique, l’aponévrotomie endoscopique de
          l’avant-bras&nbsp;», in Rodineau J, Saillant G. Masson,{" "}
          <i>Arthroscopie thérapeutique en traumatologie du sport</i>, 2005,
          pp.&nbsp;147-154.
        </Li>

        <Li>
          P. Roure, D. Fontes, «&nbsp;Articulation trapézo-métacarpienne et
          articulations des doigts : apport de l’arthroscopie&nbsp;», in
          Rodineau J., Saillant G. Masson,{" "}
          <i>Arthroscopie thérapeutique en traumatologie du sport</i>, 2005,
          pp.&nbsp;137-146.
        </Li>

        <Li>
          P. Roure, D. Fontes, «&nbsp;Apports de l’arthroscopie dans le
          traitement des fractures articulaires du radius chez le sujet
          jeune&nbsp;», in Rodineau J., Saillant G. Masson,{" "}
          <i>Arthroscopie thérapeutique en traumatologie du sport</i>, 2005,
          pp.&nbsp;109-116.
        </Li>

        <Li>
          P. Roure, D. Fontes, «&nbsp;Apports actuels de l’arthroscopie dans la
          pathologie du poignet&nbsp;»,{" "}
          <i>Index Orthopédie-Traumatologie du sport</i>, 2005, vol 2, n4,
          pp.&nbsp;5-15.
        </Li>

        <Li>
          P. Roure, D. Fontes, «&nbsp;Apports de l’arthroscopie dans la
          pseudarthrose du scaphoide,&nbsp;», in Lussiez B., Rizzo C., Lebreton
          E., <i>Le scaphoide, de la fracture à l’arthrose</i>, Sauramps med,
          2004, pp.&nbsp;357-364.
        </Li>

        <Li>
          P. Roure, D. Fontes, «&nbsp;Le poignet arthrosique du sportif : les
          séquelles de fractures&nbsp;», in Landreau P, Garçon P.,{" "}
          <i>Sport et cartilage</i>, Med line Ed., 2004, pp.&nbsp;165-175.
        </Li>

        <Li>
          P. Roure, A. C. Masquelet, «&nbsp;Etude anatomique du coussin
          graisseux sus-rétinaculaire : application à l’abord chirurgical du
          canal carpien&nbsp;», <i>Chirurgie de la main</i>, 2001, vol 20, 4,
          pp.&nbsp;287-293.
        </Li>

        <Li>
          P. Roure, B. Barbato, «&nbsp;Conduite à tenir en urgence devant une
          lésion pulpaire des doigts&nbsp;», <i>Maîtrise Orthopédique</i>,
          n°103, pp.&nbsp;14-16.
        </Li>

        <Li>
          P. Roure, «&nbsp;5eme cours de chirurgie de l’épaule de Balgrist,
          Zürich&nbsp;», <i>Maîtrise Orthopédique</i>, n°89, pp.&nbsp;24-27.
        </Li>

        <Li>
          P. Roure, W. Y. Ip, W. Lu, S.P. Chow, S. Gogolewski,
          «&nbsp;Intramedullary fixation by resorbable rods in a comminuted
          phalangeal fracture model&nbsp;», <i>Journal of Hand Surgery</i>, 24B
          : 4, pp.&nbsp;476-481.
        </Li>

        <Li>
          P. Roure, «&nbsp;Bons Baisers de Hong-Kong, région administrative
          spéciale de la République Populaire de Chine&nbsp;»,{" "}
          <i>Maîtrise Orthopédique</i>, 1999, n°81, pp.&nbsp;14-19.
        </Li>

        <Li>
          P. Roure. (1999). «&nbsp;La cicatrisation dirigée&nbsp;»,{" "}
          <i>Développement et santé</i>, n°144, pp.&nbsp;28-30.
        </Li>
      </Ul>
      <Share
        slug="https://www.docteurphilipperoure.com/en/overview/"
        title="Dr Roure, Orthopedic surgeon of the upper limb in Paris"
      />
    </Wrapper>
  </Layout>
)

export default IndexPage

const WrapperTitle = styled.div`
  background-color: white;
  width: 78%;
  margin: 0 auto;
  border-radius: 7px;
  padding: 1.5em 2em 0.9em;
  margin-bottom: 45px;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 1px 7px;
  @media (max-width: 1024px) {
    width: 100%;
    box-sizing: border-box;
    padding: 1em 1em 0.5em;
  }
`
const Breadcrumb = styled.div`
  text-align: center;
  font-size: 0.9em;
  line-height: 1em;
  font-weight: 400;
  a {
    color: #919090;
    text-decoration: none;
  }
  @media (max-width: 678px) {
    font-size: 0.87em;
    margin-bottom: 10px;
  }
`

const H1 = styled.h1`
  font-size: 2.1em;
  @media (max-width: 678px) {
    font-size: 1.82em;
    line-height: 1.1em;
  }
`

const H2 = styled.h2`
  font-weight: 400;
  line-height: 1.38em;
  font-size: 1.72em;
  margin-block-start: 0;
  span {
    font-weight: 800;
  }
  @media (max-width: 678px) {
    font-size: 1.7em;
    line-height: 1.3em;
  }
`

const H3 = styled.h3`
  text-align: center;
  font-weight: 400;
  line-height: 1.38em;
  font-size: 2.1em;
  margin-bottom: 0.4em;
  span {
    font-weight: 700;
  }
  @media (max-width: 678px) {
    font-size: 2em;
    margin-top: 0;
  }
`

const Wrapper = styled.div`
  background-color: white;
  border-radius: 5px;
  margin: 0.5em auto 0.5em;
  padding: 2.25em 2em 1em 2em;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 1px 7px;
  @media (max-width: 678px) {
    border-radius: 0;
    padding: 1em;
  }
`

const Row = styled.div`
  display: flex;
  margin: 0px 0 55px;
  clear: both;
  @media (max-width: 678px) {
    flex-wrap: wrap;
    margin: 0px 0 25px;
  }
`

const Col = styled.div`
  flex: 50%;
  @media (max-width: 678px) {
    flex-basis: 100%;
  }
`

const Col1 = styled.div`
  flex: 50%;
  @media (max-width: 678px) {
    flex-basis: 100%;
  }
`

const Col2 = styled.div`
  flex: 50%;
  padding-left: 25px;
  @media (max-width: 678px) {
    padding-left: 0;
    flex-basis: 100%;
  }
`

const Col3 = styled.div`
  flex: 33.3%;
  @media (max-width: 678px) {
    order: 2;
    flex-basis: 100%;
  }
`

const Col4 = styled.div`
  flex: 66.6%;
  padding-left: 25px;
  @media (max-width: 678px) {
    order: 1;
    flex-basis: 100%;
    padding: 0;
  }
`

const Col5 = styled.div`
  flex: 60%;
  padding-right: 20px;
  @media (max-width: 678px) {
    flex-basis: 100%;
    padding: 0;
  }
`

const Col6 = styled.div`
  flex: 40%;
  @media (max-width: 678px) {
    flex-basis: 100%;
    padding: 0;
  }
`

const Ul = styled.ul`
  padding-left: 20px;
  list-style-type: square;
  @media (max-width: 678px) {
    margin: 0;
  }
`

const Li = styled.li`
  margin-bottom: 6px;
`
